import { useState } from 'react';
import { useMutation } from 'react-query';
import styled from '@emotion/styled';
import { css, keyframes } from '@emotion/react';

import { tokens, useNotify } from '@hotjar/hotsauce';
import { InfoBox } from '@hotjar/webapp/components/InfoBox/InfoBox';
import { PaymentIcon as Icon, Name } from '@hotjar/webapp/components/PaymentIcons/PaymentIcons';
import { useTranslationWithPrefix } from '@hotjar/translations';
import type { CustomerPaymentMethod } from '@hotjar/webapp/domains/billing/customers/customer.types';
import { PaymentMethodType } from '@hotjar/webapp/domains/billing/customers/customer.types';
import { useCustomer } from '@hotjar/webapp/domains/billing/customers/api';
import { openPaymentDetails } from '@hotjar/checkout';
import { ordersApi } from '@hotjar/webapp/domains/billing/orders';
import type { OwnedAccount } from '@hotjar/account';
import * as S from '../SubscriptionSummary.styles';

interface Props {
  account: OwnedAccount;
}

const opacityAnimation = keyframes`
 0%, 100% { opacity: 1 }
 50% { opacity: .4 }
`;

const showLoader = (loading: boolean) =>
  loading &&
  css`
    &:before,
    &:after {
      content: '';
      position: absolute;
      background: ${tokens.color.ui_background};
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
    }

    &:after {
      animation: ${opacityAnimation} 4s infinite;
      background: ${tokens.color.neutral_300};
    }
  `;

const Content = styled.div`
  display: flex;
`;

const SubText = styled.span`
  position: relative;
  color: ${tokens.color.text_light};
  font-size: 12px;
  line-height: 140%;
  letter-spacing: 0.1px;
  padding: 0;
  margin-left: ${tokens.spacer.x5};
  white-space: nowrap;
`;

const PaymentLabel = styled.span`
  position: relative;
  margin-left: 8px;
`;

interface PaymentDetailsContent {
  body: string | JSX.Element | null;
  footer?: string | JSX.Element | null;
}

const formatCardNumber = (cardNumber: string): string =>
  `•••• •••• •••• ${cardNumber.replace(/\D/g, '')}`;

export const SubscriptionPaymentDetails = ({ account }: Props): JSX.Element | null => {
  const { t: tSummary } = useTranslationWithPrefix('billing:payment-details.summary');
  const { refetch, data: { paymentMethod, identifier } = {} } = useCustomer(account?.id);
  const { showToast } = useNotify();
  const [loading, setLoading] = useState(false);

  const updatePaymentMethod = useMutation(
    () =>
      openPaymentDetails(true, {
        account,
        costSpecs: [],
        hideBillingCycleSelector: true,
        customerId: identifier,
        referrer: 'Plans and Billing: Change payment method button',
      }),
    {
      onSuccess: async ({ refId }) => {
        setLoading(true);
        await ordersApi.setDefaultPaymentMethod({ accountId: account.id, paymentMethodId: refId });
        // get and display the newly assigned payment method from the customer object
        await refetch();
        showToast({ kind: 'success', text: tSummary('change.notify-success') });
      },
      onError: () => {
        showToast({ kind: 'danger', text: tSummary('change.notify-failed') });
      },
      onSettled: () => setLoading(false),
    },
  );

  const renderPaymentContent = (
    method: CustomerPaymentMethod,
    isLoading: boolean,
  ): PaymentDetailsContent => {
    if (method.paymentMethodType === PaymentMethodType.CREDIT_CARD) {
      const { cardNumber, expirationMonth, expirationYear } = method.details;
      return {
        body: (
          <S.ContentSection>
            <Content>
              <Icon name={Name.default} alt="credit-card" />
              <PaymentLabel css={showLoader(isLoading)}>
                {' '}
                {formatCardNumber(cardNumber)}
              </PaymentLabel>
            </Content>
            <SubText css={showLoader(isLoading)}>
              {tSummary('card-expires', { date: `${expirationMonth}/${expirationYear}` })}
            </SubText>
          </S.ContentSection>
        ),
        footer: (
          <S.SwitchLink
            id="btn-set-payment-method"
            onClick={() => {
              updatePaymentMethod.mutate();
            }}
            variant="primary"
          >
            {tSummary('change.action')}
          </S.SwitchLink>
        ),
      };
    }
    return { body: null };
  };

  return paymentMethod ? (
    <InfoBox
      {...renderPaymentContent(paymentMethod, loading)}
      title={<S.Header>{tSummary('payment-method')}</S.Header>}
    />
  ) : null;
};
